import React, { useState, useContext, useMemo, useCallback } from 'react'
import { Grid, Button, Box } from '@mui/material'
import { translate } from '../i18n'
import { updateEmail, revertEmail } from '../api'
import { EmailRequest, User } from '../model'
import { FeedbackContext } from '../feedback'
import { AnyInput, useValidatedForm } from '../anyform'
import { AppContext } from '../context'
import { SimpleSwitch, Surface, EmailIcon } from '../components'
import { NotEqualsToValidator } from '../validators'

export default function UserEmailForm() {
    const feedbackContext = useContext(FeedbackContext)
    const context = useContext(AppContext)
    const sessionUser = context.session?.user

    const { register, bind, isValid, setPristine } =
        useValidatedForm<EmailRequest>()
    const [user, setUser] = useState<User>(sessionUser!)

    const validators = useMemo(() => {
        return [new NotEqualsToValidator(user.email)]
    }, [user])

    const [update, setUpdate] = useState(!!user.new_email)

    const updateUser = useCallback(
        (user: User) => {
            if (!context.session) return

            const curr = context.session.user
            const newUser = {
                ...curr,
                email: user.email,
                new_email: user.new_email,
            }

            context.session.user = newUser
            setUser(newUser)
        },
        [context.session]
    )

    const hasChangeSwitch = useCallback((name: string, checked: boolean) => {
        setUpdate(checked)
    }, [])

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!isValid()) {
            return
        }

        feedbackContext.showBackdrop()

        const request = bind()
        updateEmail(request)
            .then((user) => {
                feedbackContext.showSuccess(
                    translate('users.profile.updating_email', {
                        email: request.email,
                    }) as string
                )
                updateUser(user)
            })
            .catch((error) => feedbackContext.showError(error.message))
            .finally(feedbackContext.closeBackdrop)
    }

    const onCancel = () => {
        feedbackContext.showBackdrop()
        revertEmail()
            .then((user) => {
                feedbackContext.showSuccess(
                    translate('users.profile.reverted_email', {
                        email: user!.email,
                    }) as string
                )
                setPristine()
                setUpdate(false)
                updateUser(user)
            })
            .catch((error) => feedbackContext.showError(error.message))
            .finally(feedbackContext.closeBackdrop)
    }

    return (
        <Surface
            title={translate('users.profile.email') as string}
            icon={<EmailIcon />}
        >
            <Grid
                container
                alignItems="center"
                justifyContent="center"
                alignContent="center"
            >
                <Grid item xs={12}>
                    <form autoComplete="off" noValidate onSubmit={onSubmit}>
                        <Grid container>
                            <Grid item xs={12}>
                                <AnyInput
                                    type="email"
                                    name="old_email"
                                    label={
                                        translate('users.old_email') as string
                                    }
                                    initialValue={user.email}
                                    disabled
                                    required
                                    register={register}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container justifyContent="flex-end">
                                    <Grid item xs="auto">
                                        <SimpleSwitch
                                            value="update"
                                            label={
                                                translate(
                                                    'users.profile.change_email'
                                                ) as string
                                            }
                                            checked={update}
                                            onChanged={hasChangeSwitch}
                                            disabled={!!user.new_email}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                            {update && (
                                <Grid item xs={12}>
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <AnyInput
                                                type="email"
                                                name="email"
                                                label={
                                                    translate(
                                                        'users.new_email'
                                                    ) as string
                                                }
                                                initialValue={user.new_email}
                                                validators={validators}
                                                required
                                                disabled={!!user.new_email}
                                                register={register}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box my={2}>
                                                {user.new_email ? (
                                                    <Button
                                                        variant="outlined"
                                                        color="primary"
                                                        fullWidth
                                                        size="large"
                                                        onClick={onCancel}
                                                    >
                                                        {translate(
                                                            'buttons.cancel'
                                                        )}
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        type="submit"
                                                        variant="contained"
                                                        color="primary"
                                                        fullWidth
                                                        size="large"
                                                    >
                                                        {translate(
                                                            'buttons.update'
                                                        )}
                                                    </Button>
                                                )}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                    </form>
                </Grid>
            </Grid>
        </Surface>
    )
}
